import { useEffect, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from "react-i18next";
import { MealPlanEntry } from "../../models/MealPlanEntry";
import { getRecipeThumbnail } from "../../utils/RecipeService";

export type MealPlanCellProps = {
  entry?: MealPlanEntry;
  dayLabel: string;
  mealLabel: string;
  onEmptyClick: () => void;
  onEntryClick: (anchorEl: HTMLElement) => void;
};

const MealPlanCell = (props: MealPlanCellProps) => {
  const { t } = useTranslation();
  const [thumbnail, setThumbnail] = useState<string>();

  useEffect(() => {
    if (props.entry && !thumbnail) {
      getRecipeThumbnail(props.entry.recipeId).then((image: Blob | null) => {
        if (image) setThumbnail(URL.createObjectURL(image));
      });
    }
  }, [props.entry, thumbnail]);

  if (!props.entry) {
    return (
      <Box
        role="button"
        aria-label={`${t('selectRecipe')} – ${props.mealLabel} ${props.dayLabel}`}
        onClick={props.onEmptyClick}
        sx={{
          flex: 1, height: 64,
          border: '2px dashed', borderColor: 'divider',
          borderRadius: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'text.disabled',
          transition: 'border-color 0.15s, background 0.15s',
          '&:hover': {
            borderColor: 'primary.light',
            backgroundColor: (theme) => theme.palette.mode === 'light'
              ? 'rgba(217, 119, 6, 0.05)'
              : 'rgba(245, 158, 11, 0.07)',
          },
        }}
      >
        <AddIcon sx={{ fontSize: 18 }} />
      </Box>
    );
  }

  return (
    <Paper
      elevation={2}
      role="button"
      aria-label={`${props.entry.recipeName} – ${props.mealLabel} ${props.dayLabel}`}
      onClick={(e) => props.onEntryClick(e.currentTarget as HTMLElement)}
      sx={{
        flex: 1, height: 64, p: 0.5,
        borderRadius: 2,
        display: 'flex', flexDirection: 'column', gap: '3px',
        cursor: 'pointer', overflow: 'hidden',
        transition: 'transform 0.12s, box-shadow 0.12s',
        '&:hover': { transform: 'scale(1.02)' },
      }}
    >
      <Box
        component="img"
        src={thumbnail ?? 'https://wiki.dave.eu/images/4/47/Placeholder.png'}
        alt={props.entry.recipeName}
        sx={{ width: '100%', height: 32, objectFit: 'cover', borderRadius: 1.5, flexShrink: 0 }}
      />
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem', lineHeight: 1.2, fontWeight: 600,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: 'text.primary', px: 0.25,
        }}
      >
        {props.entry.recipeName}
      </Typography>
    </Paper>
  );
};

export default MealPlanCell;
